import React from 'react';
import { Box, Button, Typography, DialogContent, Stack } from '@mui/material';
import ComputerIcon from '@mui/icons-material/Computer';
import DownloadIcon from '@mui/icons-material/Download';
import { useNavigate } from 'react-router-dom';
import DialogWindow from './DialogWindow';
import { desktopBridge } from '@/services/desktopBridge';

/** Maps internal action keys to readable verb phrases shown in the dialog body. */
const ACTION_LABELS: Record<string, string> = {
  fileSync: 'sync project files',
  commit: 'create a new version',
  selectFolder: 'select a project folder',
  fileOpen: 'open this file',
  fileDelete: 'delete local files',
  versionSync: 'sync to this version',
  autoLocate: 'auto-locate your project folder',
  locateProject: 'locate your project folder',
};

export interface DesktopAppRequiredDialogProps {
  open: boolean;
  /** One of the ACTION_LABELS keys, or any freeform string used as a fallback label. */
  action: string;
  onClose: () => void;
}

export const DesktopAppRequiredDialog: React.FC<
  DesktopAppRequiredDialogProps
> = ({ open, action, onClose }) => {
  const navigate = useNavigate();
  const actionLabel =
    ACTION_LABELS[action] ??
    action
      .replace(/([A-Z])/g, ' $1')
      .toLowerCase()
      .trim();

  const handleLaunch = () => {
    // Attempt to open the already-installed desktop app via its custom protocol.
    window.location.href = 'studio-vault://';
    // After a short pause, probe the bridge in case the app was just launched.
    setTimeout(() => {
      void desktopBridge.tryConnect();
    }, 3000);
    onClose();
  };

  const handleDownload = () => {
    onClose();
    navigate('/download');
  };

  return (
    <DialogWindow
      open={open}
      onClose={onClose}
      title="Desktop App Required"
      maxWidth="xs"
      fullWidth
      hideExpand
    >
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1, pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
            <ComputerIcon
              sx={{ fontSize: 56, color: 'primary.main', opacity: 0.85 }}
            />
          </Box>

          <Typography variant="body1" textAlign="center">
            To {actionLabel}, you need the Studio Vault desktop app.
          </Typography>

          <Typography variant="body2" color="text.secondary" textAlign="center">
            If you already have the app installed, launch it and try again.
            Otherwise, download it below.
          </Typography>

          <Button
            variant="contained"
            color="primary"
            onClick={handleLaunch}
            startIcon={<ComputerIcon />}
            fullWidth
            data-cy="launch-desktop-app"
          >
            Launch Desktop App
          </Button>

          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
            sx={{ my: -0.5 }}
          >
            OR
          </Typography>

          <Button
            variant="outlined"
            onClick={handleDownload}
            startIcon={<DownloadIcon />}
            fullWidth
            data-cy="download-desktop-app"
          >
            Download Desktop App
          </Button>
        </Stack>
      </DialogContent>
    </DialogWindow>
  );
};
